"use client";

import { useEffect, useRef } from "react";

interface MicWaveformProps {
  stream: MediaStream;
}

const BAR_WIDTH = 4;
const BAR_GAP = 3;
const BAR_COUNT = 30; // 显示的柱子数量

export default function MicWaveform({ stream }: MicWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const barHeightsRef = useRef<number[]>(Array(BAR_COUNT).fill(0));

  useEffect(() => {
    if (!stream) return;
    let animationId: number;
    let analyser: AnalyserNode;
    let dataArray: Uint8Array<ArrayBuffer>;
    let bufferLength: number;
    let source: MediaStreamAudioSourceNode;

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    async function setupAudio() {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioCtx;

      source = audioCtx.createMediaStreamSource(stream);
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256; // 更小的fftSize，适合做柱状图

      bufferLength = analyser.frequencyBinCount;
      dataArray = new Uint8Array(bufferLength);

      source.connect(analyser);

      function draw() {
        animationId = requestAnimationFrame(draw);
        analyser.getByteFrequencyData(dataArray);

        if (!ctx || !canvas) return;

        // 获取当前音量（取前几个频率的均值）
        const avg = dataArray.slice(0, 16).reduce((a, b) => a + b, 0) / 16;
        // 归一化到0~1
        const norm = Math.min(1, avg / 128);

        // 生成一个新的柱子高度（最小6，最大canvas.height）
        const newBarHeight = Math.max(6, norm * canvas.height);

        // 柱子向左移动，右侧插入新高度
        barHeightsRef.current = [
          ...barHeightsRef.current.slice(1),
          newBarHeight,
        ];

        // 清空画布
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // 画所有柱子（从右到左）
        for (let i = 0; i < BAR_COUNT; i++) {
          const barHeight = barHeightsRef.current[BAR_COUNT - 1 - i];
          const x = canvas.width - (i + 1) * (BAR_WIDTH + BAR_GAP);

          // 渐变色：两侧浅色，中间深色
          let color = '#E0E0E0';

          // 画圆角矩形
          const radius = 2;
          const y = canvas.height - barHeight;
          ctx.beginPath();
          ctx.moveTo(x + radius, y);
          ctx.lineTo(x + BAR_WIDTH - radius, y);
          ctx.quadraticCurveTo(x + BAR_WIDTH, y, x + BAR_WIDTH, y + radius);
          ctx.lineTo(x + BAR_WIDTH, y + barHeight - radius);
          ctx.quadraticCurveTo(x + BAR_WIDTH, y + barHeight, x + BAR_WIDTH - radius, y + barHeight);
          ctx.lineTo(x + radius, y + barHeight);
          ctx.quadraticCurveTo(x, y + barHeight, x, y + barHeight - radius);
          ctx.lineTo(x, y + radius);
          ctx.quadraticCurveTo(x, y, x + radius, y);
          ctx.closePath();
          ctx.fillStyle = color;
          ctx.fill();
        }
      }

      draw();
    }

    setupAudio();

    return () => {
      cancelAnimationFrame(animationId);
      audioContextRef.current?.close();
    };
  }, [stream]);

  return (
    <div className="justify-center items-center">
      <canvas
        ref={canvasRef}
        width={BAR_COUNT * (BAR_WIDTH + BAR_GAP)}
        height={25}
      />
    </div>
  );
}
